import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth, db, googleProvider, functions } from '../firebase';
import { createUserWithEmailAndPassword, signInWithPopup, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc, addDoc, collection, getDoc, deleteDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { friendlyError } from '../utils/friendlyError';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { validateSignupForm } from '../utils/signupValidation';

// Reusable signup form. Drives both the full-page /studio/register route and
// the landing-page modal. The hero copy block is hidden in `compact` mode so
// the modal layout doesn't double up on the trigger context. `showSignInLink`
// is suppressed by the full-page route, which already shows it in its header.
function SignupForm({ onSuccess, compact = false, showHero = true, showSignInLink = true }) {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [notice, setNotice] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [passwordFeedback, setPasswordFeedback] = useState('');
    const [passwordsMatch, setPasswordsMatch] = useState(false);
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [honeypot, setHoneypot] = useState('');
    const [formStartTime] = useState(Date.now());
    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        setNotice('');
        setValidationErrors({});
    }, [email, password, confirmPassword, firstName, lastName]);

    useEffect(() => {
        setIsEmailValid(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    }, [email]);

    useEffect(() => {
        if (confirmPassword) setPasswordsMatch(password === confirmPassword);
    }, [password, confirmPassword]);

    const isFormValid = email && password && confirmPassword && firstName && lastName;

    const handleSuccess = () => {
        if (onSuccess) onSuccess();
        else navigate('/studio/setup', { replace: true, state: { fromSignup: true } });
    };

    const signupWithUsernameAndPassword = async (e) => {
        e.preventDefault();

        const validation = validateSignupForm(
            { email, firstName, lastName, companyName: '', password, confirmPassword, honeypot },
            formStartTime
        );

        if (!validation.isValid) {
            if (validation.errors.general) setNotice(validation.errors.general);
            else { setValidationErrors(validation.errors); setNotice('Please fix the errors below and try again.'); }
            return;
        }

        if (password !== confirmPassword) {
            setNotice("Passwords don't match. Please try again.");
            return;
        }

        setIsProcessing(true);
        let createdUser = null;
        let createdWorkspaceRef = null;
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            createdUser = userCredential.user;

            createdWorkspaceRef = await addDoc(collection(db, 'workspace'), {
                ownerId: createdUser.uid,
                name: '',
                createdAt: new Date(),
                gameCredits: 0,
            });

            await setDoc(doc(db, 'users', createdUser.uid), {
                uid: createdUser.uid,
                email: createdUser.email,
                firstName,
                lastName,
                companyId: createdWorkspaceRef.id,
                createdAt: new Date(),
                authProvider: 'email',
                emailVerified: false,
                onboardingComplete: false,
            });

            try { await sendEmailVerification(createdUser); }
            catch (verifyError) { console.error('Verification email error:', verifyError); }

            try {
                const sendWelcomeEmail = httpsCallable(functions, 'sendWelcomeEmail', { region: 'europe-west1' });
                sendWelcomeEmail({ userId: createdUser.uid }).catch(console.error);
            } catch (emailError) { console.error('Welcome email error:', emailError); }

            handleSuccess();
        } catch (error) {
            console.error('Signup error:', error);
            if (createdWorkspaceRef) {
                try { await deleteDoc(createdWorkspaceRef); }
                catch (wsDeleteError) { console.error('Failed to roll back workspace doc:', wsDeleteError); }
            }
            if (createdUser) {
                try { await createdUser.delete(); }
                catch (deleteError) { console.error('Failed to roll back auth user:', deleteError); }
                try { await auth.signOut(); }
                catch (signOutError) { console.error('Error signing out after rollback:', signOutError); }
            }
            setNotice(friendlyError(error));
            setIsProcessing(false);
        }
    };

    const checkPasswordStrength = (pwd) => {
        let strength = 0;
        const feedback = [];
        if (pwd.length >= 8) strength += 1; else feedback.push('At least 8 characters');
        if (/[A-Z]/.test(pwd)) strength += 1; else feedback.push('One uppercase letter');
        if (/[a-z]/.test(pwd)) strength += 1; else feedback.push('One lowercase letter');
        if (/[0-9]/.test(pwd)) strength += 1; else feedback.push('One number');
        if (/[^A-Za-z0-9]/.test(pwd)) strength += 1; else feedback.push('One special character');
        setPasswordStrength(strength);
        setPasswordFeedback(feedback.join(' • '));
    };

    useEffect(() => {
        if (password) checkPasswordStrength(password);
        else { setPasswordStrength(0); setPasswordFeedback(''); }
    }, [password]);

    const handleGoogleSignup = async () => {
        try {
            const result = await signInWithPopup(auth, googleProvider);
            const user = result.user;
            setIsProcessing(true);

            const userDoc = await getDoc(doc(db, 'users', user.uid));

            if (!userDoc.exists()) {
                let createdWorkspaceRef = null;
                try {
                    const nameParts = user.displayName ? user.displayName.split(' ') : ['', ''];
                    const googleFirstName = nameParts[0] || '';
                    const googleLastName = nameParts.slice(1).join(' ') || '';

                    createdWorkspaceRef = await addDoc(collection(db, 'workspace'), {
                        ownerId: user.uid,
                        name: '',
                        createdAt: new Date(),
                        gameCredits: 0,
                    });

                    await setDoc(doc(db, 'users', user.uid), {
                        uid: user.uid,
                        email: user.email,
                        firstName: googleFirstName,
                        lastName: googleLastName,
                        companyId: createdWorkspaceRef.id,
                        createdAt: new Date(),
                        authProvider: 'google',
                        emailVerified: true,
                        onboardingComplete: false,
                    });

                    if (!auth.currentUser.emailVerified) await auth.currentUser.reload();

                    try {
                        const sendWelcomeEmail = httpsCallable(functions, 'sendWelcomeEmail', { region: 'europe-west1' });
                        sendWelcomeEmail({ userId: user.uid }).catch(console.error);
                    } catch (emailError) { console.error('Welcome email error:', emailError); }

                    handleSuccess();
                } catch (dbError) {
                    console.error('Database error:', dbError);
                    if (createdWorkspaceRef) {
                        try { await deleteDoc(createdWorkspaceRef); }
                        catch (wsDeleteError) { console.error('Failed to roll back workspace doc:', wsDeleteError); }
                    }
                    await auth.signOut();
                    setNotice('Failed to create account. Please try again or contact support.');
                    setIsProcessing(false);
                }
            } else {
                // Existing account: sign in and route appropriately. The modal's
                // onSuccess is intentionally ignored here - an existing account
                // shouldn't land on /studio/setup.
                const userData = userDoc.data();
                if (!userData.emailVerified) {
                    await setDoc(doc(db, 'users', user.uid), { ...userData, emailVerified: true }, { merge: true });
                }
                if (!auth.currentUser.emailVerified) await auth.currentUser.reload();
                if (userData.onboardingComplete === false) navigate('/studio/setup', { replace: true });
                else navigate('/studio/dashboard', { replace: true });
            }
        } catch (error) {
            console.error('Google signup error:', error);
            setIsProcessing(false);
            if ((error.code === 'auth/account-exists-with-different-credential' || error.code === 'auth/email-already-in-use') && error.email) {
                setTimeout(() => navigate(`/login?email=${encodeURIComponent(error.email)}`), 2000);
            }
            setNotice(friendlyError(error, 'An error occurred during sign up. Please try again.'));
            try { await auth.signOut(); } catch (signOutError) { console.error('Error signing out:', signOutError); }
            setIsProcessing(false);
        }
    };

    return (
        <div className={compact ? '' : 'w-full max-w-md mx-auto'}>
            {showHero && (
                <div className="text-center mb-8">
                    <h1 className="text-3xl md:text-4xl gch-heading tracking-tight mb-3">Launch your first branded game</h1>
                    <p className="text-sm gch-text-secondary mb-5">Add your logo, colours, and leaderboard. Share it by link or QR code.</p>
                    <div className="gch-pill text-xs font-semibold px-4 py-1.5 inline-flex items-center gap-2">
                        <span>Free to build & preview</span>
                        <span className="gch-text-muted">·</span>
                        <span>No card required</span>
                    </div>
                </div>
            )}

            {notice && (
                <div className="mb-6 p-3 gch-surface rounded-[var(--admin-card-radius)] text-sm font-medium text-red-400 border border-red-500/30" style={{ background: 'rgba(229, 106, 84, 0.15)' }}>
                    {notice}
                </div>
            )}

            <div className={compact ? '' : 'gch-card p-6 sm:p-8'}>
                {!showEmailForm ? (
                    <div className="space-y-5">
                        <button
                            onClick={handleGoogleSignup}
                            disabled={isProcessing}
                            className="btn-admin-primary w-full flex items-center justify-center gap-3 disabled:opacity-50"
                        >
                            {isProcessing ? (
                                <div className="flex items-center">
                                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3" />
                                    Creating account...
                                </div>
                            ) : (
                                <>
                                    <svg className="h-5 w-5" viewBox="0 0 24 24">
                                        <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                        <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                        <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                        <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                    </svg>
                                    Sign up with Google
                                </>
                            )}
                        </button>

                        <div className="relative">
                            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[var(--admin-border)]" /></div>
                            <div className="relative flex justify-center">
                                <span className="px-3 text-xs font-medium gch-text-muted" style={{ background: compact ? 'var(--admin-card-bg)' : 'var(--admin-card-bg)' }}>OR</span>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => {
                                setShowEmailForm(true);
                                setTimeout(() => {
                                    const emailForm = document.getElementById('email-signup-form');
                                    if (emailForm) emailForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }, 100);
                            }}
                            className="btn-admin-outline btn-admin-md w-full"
                        >
                            Sign up with email
                        </button>
                    </div>
                ) : (
                    <form id="email-signup-form" onSubmit={signupWithUsernameAndPassword} className="space-y-4">
                        <input
                            type="text" name="website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
                            style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
                            tabIndex="-1" autoComplete="off"
                        />

                        <div>
                            <label htmlFor="email" className="block text-sm font-semibold leading-6 mb-2 gch-text-primary">Email</label>
                            <input
                                id="email" name="email" type="email" autoComplete="email" required
                                value={email} onChange={(e) => setEmail(e.target.value)}
                                className="gch-input-ghost block w-full px-4 py-3 gch-text-primary"
                                style={{ borderColor: email && !isEmailValid ? 'rgba(239, 68, 68, 0.6)' : undefined }}
                                placeholder="you@company.com"
                            />
                            {email && !isEmailValid && <p className="mt-2 text-sm font-medium text-red-400">Invalid email format</p>}
                            {validationErrors.email && <p className="mt-2 text-sm font-medium text-red-400">{validationErrors.email}</p>}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="firstName" className="block text-sm font-semibold leading-6 mb-2 gch-text-primary">First name</label>
                                <input id="firstName" name="firstName" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} required className="gch-input-ghost block w-full px-4 py-3 gch-text-primary" placeholder="John" />
                                {validationErrors.firstName && <p className="mt-1 text-xs font-medium text-red-400">{validationErrors.firstName}</p>}
                            </div>
                            <div>
                                <label htmlFor="lastName" className="block text-sm font-semibold leading-6 mb-2 gch-text-primary">Last name</label>
                                <input id="lastName" name="lastName" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} required className="gch-input-ghost block w-full px-4 py-3 gch-text-primary" placeholder="Doe" />
                                {validationErrors.lastName && <p className="mt-1 text-xs font-medium text-red-400">{validationErrors.lastName}</p>}
                            </div>
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-semibold leading-6 mb-2 gch-text-primary">Password</label>
                            <input id="password" name="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="gch-input-ghost block w-full px-4 py-3 gch-text-primary" placeholder="Create a secure password" />
                            {password && (
                                <div className="mt-3">
                                    <div className="flex space-x-1 mb-2">
                                        {[...Array(5)].map((_, index) => (
                                            <div key={index} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${index < passwordStrength ? passwordStrength <= 2 ? 'bg-red-500' : passwordStrength <= 3 ? 'bg-yellow-500' : 'bg-green-500' : 'bg-white/10'}`} />
                                        ))}
                                    </div>
                                    <p className={`text-xs font-medium ${passwordStrength <= 2 ? 'text-red-400' : passwordStrength <= 3 ? 'text-yellow-400' : 'text-green-400'}`}>
                                        {passwordFeedback || 'Strong password'}
                                    </p>
                                </div>
                            )}
                        </div>

                        <div>
                            <label htmlFor="confirm-password" className="block text-sm font-semibold leading-6 mb-2 gch-text-primary">Confirm password</label>
                            <div className="relative">
                                <input
                                    id="confirm-password" name="confirm-password" type="password"
                                    value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
                                    className="gch-input-ghost block w-full px-4 py-3 gch-text-primary"
                                    style={{ borderColor: confirmPassword ? (passwordsMatch ? 'rgba(34, 197, 94, 0.6)' : 'rgba(239, 68, 68, 0.6)') : undefined }}
                                    placeholder="Confirm password"
                                />
                                {confirmPassword && (
                                    <div className="absolute right-3 top-3.5">
                                        {passwordsMatch ? (
                                            <CheckCircleIcon className="h-5 w-5 text-green-400" />
                                        ) : (
                                            <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        )}
                                    </div>
                                )}
                            </div>
                            {confirmPassword && (
                                <p className={`mt-2 text-xs font-medium ${passwordsMatch ? 'text-green-400' : 'text-red-400'}`}>
                                    {passwordsMatch ? 'Passwords match' : "Passwords don't match"}
                                </p>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={!isFormValid || isProcessing}
                            className="btn-admin-primary btn-admin-md w-full disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                        >
                            {isProcessing ? (
                                <div className="flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3" />
                                    Creating account...
                                </div>
                            ) : 'Create account'}
                        </button>

                        <div className="text-center">
                            <button
                                type="button"
                                onClick={() => setShowEmailForm(false)}
                                className="text-sm font-medium gch-text-muted hover:opacity-80 transition-opacity"
                            >
                                ← Back to all sign-up options
                            </button>
                        </div>
                    </form>
                )}
            </div>

            <p className="mt-6 text-xs text-center font-medium gch-text-muted">
                {showSignInLink && (
                    <span className="block mb-2">
                        Already have an account?{' '}
                        <Link to="/studio/login" className="font-semibold text-[#C45443] hover:opacity-80 transition-opacity">Sign in</Link>
                    </span>
                )}
                By signing up, you agree to our{' '}
                <a href="/terms" className="gch-text-secondary hover:gch-text-primary transition-colors">Terms</a>
                {' '}and{' '}
                <a href="/privacy" className="gch-text-secondary hover:gch-text-primary transition-colors">Privacy Policy</a>
            </p>
        </div>
    );
}

export default SignupForm;
